import { useCallback, useMemo, useRef, useState } from 'react';
import type React from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import type { SlimUser } from 'api';
import { SecondaryNavigationSelector } from 'components/ui';

interface WorkboardUserSelectorProps {
  currentUserDisplayName: string;
  isLoading?: boolean;
  selectedUserEmail: string;
  setSelectedUserEmail: (email: string) => void;
  staffUsers: SlimUser[];
}

const getUserDisplayName = (u: SlimUser) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';

const WorkboardUserSelector = ({
  currentUserDisplayName,
  isLoading = false,
  selectedUserEmail,
  setSelectedUserEmail,
  staffUsers,
}: WorkboardUserSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearch('');
  }, []);

  const selectedUser = useMemo(
    () => staffUsers.find(u => u.email === selectedUserEmail) ?? null,
    [staffUsers, selectedUserEmail]
  );

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return staffUsers;
    const lower = search.toLowerCase();
    return staffUsers.filter(
      u =>
        u.email?.toLowerCase().includes(lower) ||
        u.first_name?.toLowerCase().includes(lower) ||
        u.last_name?.toLowerCase().includes(lower)
    );
  }, [staffUsers, search]);

  const handleToggle = useCallback(
    (email: string) => {
      setSelectedUserEmail(email === selectedUserEmail ? '' : email);
      handleClose();
    },
    [handleClose, selectedUserEmail, setSelectedUserEmail]
  );

  const displayName = selectedUser ? getUserDisplayName(selectedUser) : currentUserDisplayName;

  return (
    <>
      <SecondaryNavigationSelector
        disabled={isLoading}
        onClick={handleOpen}
        startIcon={<AccountCircleOutlinedIcon fontSize="inherit" />}
        sx={{
          '--secondary-navigation-control-height': '32px',
          '--secondary-navigation-font-size': '13px',
          '--secondary-navigation-action-icon-size': '18px',
          justifyContent: 'flex-start !important' as string,
          '& .MuiButton-endIcon': { marginLeft: 'auto' },
          minWidth: 280,
          width: 280,
        }}
        value={displayName}
        variant="default"
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={handleClose}
        open={Boolean(anchorEl)}
        slotProps={{ paper: { sx: { display: 'flex', flexDirection: 'column', maxHeight: 400, width: 300 } } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        TransitionProps={{ onEntered: () => searchRef.current?.focus() }}>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 1 }}>
          <TextField
            fullWidth
            inputRef={searchRef}
            onChange={e => setSearch(e.target.value)}
            placeholder="Find user"
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            value={search}
          />
        </Box>
        <List dense sx={{ flex: 1, overflow: 'auto', pt: 0 }}>
          {filteredUsers.map(u => (
            <ListItemButton
              key={u.email}
              onClick={() => handleToggle(u.email ?? '')}
              selected={u.email === selectedUserEmail}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  checked={u.email === selectedUserEmail}
                  disableRipple
                  edge="start"
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                primary={u.email}
                primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              />
            </ListItemButton>
          ))}
          {filteredUsers.length === 0 && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography color="text.secondary" variant="body2">
                No users found
              </Typography>
            </Box>
          )}
        </List>
      </Popover>
    </>
  );
};

export default WorkboardUserSelector;
